import React, { useState, useRef, useEffect, Fragment } from 'react';
import { TagIcon, XMarkIcon, PencilIcon } from '@heroicons/react/24/outline';
import { HiOutlineCheck } from 'react-icons/hi';
import { Dialog, Transition } from '@headlessui/react';
import { CATEGORY_COLORS, taskService } from '../services/database';

interface CategoryModalProps {
  selectedCategory: string | undefined;
  onChange: (category: string | undefined) => void;
  onCategoryRenamed?: (oldName: string, newName: string) => void;
  // onCategoryAdded?: () => void; // DISABLED FOR NOW - FUTURE FEATURE
}

const CategoryModal: React.FC<CategoryModalProps> = ({
  selectedCategory,
  onChange,
  onCategoryRenamed,
  // onCategoryAdded, // DISABLED FOR NOW - FUTURE FEATURE
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [categories, setCategories] = useState<string[]>([]);
  const [hoverCategory, setHoverCategory] = useState<string | null>(null);
  const [pendingCategory, setPendingCategory] = useState<string | undefined>(undefined);
  const [localColors, setLocalColors] = useState<Record<string, string>>({});
  
  // State for editing categories
  const [editingCategory, setEditingCategory] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const editInputRef = useRef<HTMLInputElement>(null);
  
  // State for adding new category (DISABLED FOR NOW - FUTURE FEATURE)
  // const [isAddingNew, setIsAddingNew] = useState(false);
  // const [newCategoryName, setNewCategoryName] = useState('');
  // const newCategoryInputRef = useRef<HTMLInputElement>(null);

  // Load categories when modal opens
  useEffect(() => {
    if (isModalOpen) {
      loadCategories();
      setPendingCategory(selectedCategory);
    }
  }, [isModalOpen, selectedCategory]);

  // Focus input when adding new category (DISABLED FOR NOW - FUTURE FEATURE)
  // useEffect(() => {
  //   if (isAddingNew && newCategoryInputRef.current) {
  //     newCategoryInputRef.current.focus();
  //   }
  // }, [isAddingNew]);

  // Focus input when editing category
  useEffect(() => {
    if (editingCategory && editInputRef.current) {
      editInputRef.current.focus();
      editInputRef.current.select();
    }
  }, [editingCategory]);

  const loadCategories = async () => {
    try {
      const records = await taskService.getAllCategoryRecords();
      const names = records.map((r) => r.name);
      setCategories(names.sort());
      // Seed local colors for optimistic rendering
      const colorSeed: Record<string, string> = {};
      records.forEach((r) => { colorSeed[r.name] = r.color || "gray"; });
      setLocalColors(colorSeed);
    } catch (error) {
      console.error('Failed to load categories:', error);
    }
  };

  const handleCategorySelect = (category: string | undefined) => {
    // Toggle logic: if clicking the same category, deselect it
    if (category === pendingCategory) {
      setPendingCategory(undefined);
    } else {
      setPendingCategory(category);
    }
  };

  // DISABLED FOR NOW - FUTURE FEATURE
  // const handleAddNewCategory = async (e: React.FormEvent) => {
  //   e.preventDefault();
  //   const trimmedName = newCategoryName.trim();
  //   if (trimmedName && !categories.includes(trimmedName)) {
  //     try {
  //       await taskService.addCategory(trimmedName);
  //       await loadCategories();
  //       setPendingCategory(trimmedName);
  //       setIsAddingNew(false);
  //       setNewCategoryName('');
  //       if (onCategoryAdded) {
  //         onCategoryAdded();
  //       }
  //     } catch (error) {
  //       console.error("Failed to add category:", error);
  //     }
  //   }
  // };

  const startEditingCategory = (e: React.MouseEvent, categoryName: string) => {
    e.stopPropagation();
    setEditingCategory(categoryName);
    setEditingName(categoryName);
  };

  const handleRenameCategory = async (e: React.FormEvent | React.FocusEvent) => {
    e.preventDefault();
    if (e.type === "submit") {
      // Only stop propagation on explicit submit
      e.stopPropagation(); // Prevent bubbling to parent forms (e.g., add-task)
    }
    if (!editingCategory || !editingName.trim()) {
      cancelEditing();
      return;
    }

    const newName = editingName.trim();
    if (newName === editingCategory) {
      setEditingCategory(null);
      setEditingName('');
      return;
    }

    try {
      // Immediately sync in-memory colors so the UI doesn't flash gray while pending
      const color = resolveColor(editingCategory);
      CATEGORY_COLORS[newName] = color;
      CATEGORY_COLORS[editingCategory] = color;
      setLocalColors((prev) => ({
        ...prev,
        [editingCategory]: color,
        [newName]: color,
      }));

      await taskService.renameCategory(editingCategory, newName);
      
      // If the currently selected category is being renamed, update the selection immediately
      if (pendingCategory === editingCategory) {
        setPendingCategory(newName);
        onChange(newName);
      } else {
        setPendingCategory((prev) => (prev === editingCategory ? newName : prev));
      }

      // Reload categories
      await loadCategories();
      
      setEditingCategory(null);
      setEditingName('');

      // Notify parent component that a category was renamed
      if (onCategoryRenamed) {
        await onCategoryRenamed(editingCategory, newName);
      }
    } catch (error) {
      console.error('Failed to rename category:', error);
      alert(error instanceof Error ? error.message : 'Failed to rename category');
    }
  };

  const cancelEditing = () => {
    setEditingCategory(null);
    setEditingName('');
  };

  const handleDone = () => {
    onChange(pendingCategory);
    setIsModalOpen(false);
  };

  const resolveColor = (category: string) =>
    localColors[category] ?? CATEGORY_COLORS[category] ?? 'gray';

  // Helper function to get category button styling
  const getCategoryButtonStyle = (category: string) => {
    const color = resolveColor(category);
    const colorMap: Record<string, string> = {
      blue: 'bg-blue-100 dark:bg-blue-900/20 text-blue-800 dark:text-blue-300',
      green: 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-300',
      red: 'bg-red-100 dark:bg-red-900/20 text-red-800 dark:text-red-300',
      purple: 'bg-purple-100 dark:bg-purple-900/20 text-purple-800 dark:text-purple-300',
      yellow: 'bg-yellow-100 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-300',
      gray: 'bg-gray-100 dark:bg-gray-900/20 text-gray-800 dark:text-gray-300',
    };
    return colorMap[color] || colorMap.gray;
  };

  const getCategoryStyle = (category: string) => {
    const color = resolveColor(category);
    const isSelected = pendingCategory === category;
    
    const styles = {
      blue: {
        bg: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 hover:bg-blue-200 dark:hover:bg-blue-900/50',
        border: isSelected ? 'border-gray-900 dark:border-white border-2' : 'border-blue-200 dark:border-blue-800 border-2',
        dot: 'bg-blue-500'
      },
      green: {
        bg: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 hover:bg-green-200 dark:hover:bg-green-900/50',
        border: isSelected ? 'border-gray-900 dark:border-white border-2' : 'border-green-200 dark:border-green-800 border-2',
        dot: 'bg-green-500'
      },
      red: {
        bg: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 hover:bg-red-200 dark:hover:bg-red-900/50',
        border: isSelected ? 'border-gray-900 dark:border-white border-2' : 'border-red-200 dark:border-red-800 border-2',
        dot: 'bg-red-500'
      },
      purple: {
        bg: 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 hover:bg-purple-200 dark:hover:bg-purple-900/50',
        border: isSelected ? 'border-gray-900 dark:border-white border-2' : 'border-purple-200 dark:border-purple-800 border-2',
        dot: 'bg-purple-500'
      },
      yellow: {
        bg: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 hover:bg-yellow-200 dark:hover:bg-yellow-900/50',
        border: isSelected ? 'border-gray-900 dark:border-white border-2' : 'border-yellow-200 dark:border-yellow-800 border-2',
        dot: 'bg-yellow-500'
      },
      gray: {
        bg: 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700',
        border: isSelected ? 'border-gray-900 dark:border-white border-2' : 'border-gray-200 dark:border-gray-600 border-2',
        dot: 'bg-gray-500'
      },
    };
    return styles[color as keyof typeof styles] || styles.gray;
  };

  const getIconColor = (category: string) => {
    const color = resolveColor(category);
    const iconColors = {
      blue: 'text-blue-700 hover:text-blue-800 dark:text-blue-300 dark:hover:text-blue-200',
      green: 'text-green-700 hover:text-green-800 dark:text-green-300 dark:hover:text-green-200',
      red: 'text-red-700 hover:text-red-800 dark:text-red-300 dark:hover:text-red-200',
      purple: 'text-purple-700 hover:text-purple-800 dark:text-purple-300 dark:hover:text-purple-200',
      yellow: 'text-yellow-700 hover:text-yellow-800 dark:text-yellow-300 dark:hover:text-yellow-200',
      gray: 'text-gray-700 hover:text-gray-800 dark:text-gray-300 dark:hover:text-gray-200',
    };
    return iconColors[color as keyof typeof iconColors] || iconColors.gray;
  };

  return (
    <>
      {/* Conditional Category Button - show icon when nothing selected, show label when category selected */}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setIsModalOpen(true);
        }}
        className={selectedCategory 
          ? `inline-flex items-center px-2 py-0.5 rounded text-xs font-medium truncate ${getCategoryButtonStyle(selectedCategory)}` 
          : "p-1 text-gray-400 dark:text-dark-secondary hover:text-gray-600 dark:hover:text-dark-primary focus:outline-none flex-shrink-0"
        }
        aria-label={selectedCategory ? `Change category from ${selectedCategory}` : "Select category"}
        title={selectedCategory || undefined}
      >
        {selectedCategory ? (
          selectedCategory
        ) : (
          <TagIcon className="h-5 w-5" />
        )}
      </button>

      {/* Category Modal */}
      <Transition appear show={isModalOpen} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-[1100]"
          onClose={() => setIsModalOpen(false)}
        >
          {/* Backdrop removed - no background darkening */}

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-lg transform overflow-hidden rounded-2xl bg-white dark:bg-dark-card p-6 text-left align-middle shadow-xl transition-all">
                  {/* Header */}
                  <div className="flex items-center justify-between mb-4">
                    <Dialog.Title
                      as="h3"
                      className="text-lg font-medium leading-6 text-gray-900 dark:text-dark-primary"
                    >
                      Select Category
                    </Dialog.Title>
                    <button
                      type="button"
                      onClick={() => setIsModalOpen(false)}
                      className="text-gray-400 dark:text-dark-secondary hover:text-gray-500 dark:hover:text-dark-primary focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded-lg p-1"
                    >
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  </div>

                  {/* Categories Grid */}
                  <div className="grid grid-cols-2 gap-2 mb-4">
                    {categories.map((category) => {
                      const categoryStyle = getCategoryStyle(category);
                      const isEditing = editingCategory === category;

                      if (isEditing) {
                        return (
                            <form
                              key={category}
                              onSubmit={handleRenameCategory}
                              className={`
                              relative flex items-center justify-between px-3 py-2.5 rounded-lg
                              transition-all duration-200 text-sm font-medium group
                              ${categoryStyle.bg}
                            `}
                          >
                            <div className="flex items-center space-x-2 min-w-0 flex-1">
                              <span 
                                className={`inline-block h-2.5 w-2.5 rounded-full ${categoryStyle.dot} flex-shrink-0`}
                              />
                              <input
                                ref={editInputRef}
                                type="text"
                                value={editingName}
                                onChange={(e) => setEditingName(e.target.value)}
                                onBlur={handleRenameCategory}
                                onKeyDown={(e) => {
                                  if (e.key === 'Escape') {
                                    cancelEditing();
                                  }
                                }}
                                className="flex-1 bg-transparent border-none outline-none font-medium text-sm min-w-0"
                                placeholder="Category name"
                              />
                            </div>
                            
                            <div className="flex items-center space-x-1 flex-shrink-0">
                              <button
                                type="button"
                                onMouseDown={(e) => {
                                  e.preventDefault(); // prevent blur/save
                                  cancelEditing();
                                }}
                                className="p-1 text-red-700 hover:text-red-900 dark:text-red-300 dark:hover:text-red-100 opacity-80 hover:opacity-100 transition-opacity duration-150 rounded-full"
                                aria-label="Cancel editing"
                              >
                                <XMarkIcon className="h-4 w-4" />
                              </button>
                            </div>
                          </form>
                        );
                      }

                      return (
                        <button
                          key={category}
                          onMouseEnter={() => setHoverCategory(category)}
                          onMouseLeave={() => setHoverCategory(null)}
                          className={`
                            relative flex items-center justify-between px-3 py-2.5 rounded-lg cursor-pointer 
                            transition-all duration-200 text-sm font-medium group
                            ${categoryStyle.bg} ${categoryStyle.border}
                          `}
                          onClick={() => handleCategorySelect(category)}
                        >
                          <div className="flex items-center space-x-2 min-w-0 flex-1">
                            <span 
                              className={`inline-block h-2.5 w-2.5 rounded-full ${categoryStyle.dot} flex-shrink-0`}
                            />
                            <span className="truncate text-left">
                              {category}
                            </span>
                          </div>
                          
                          <div className="flex items-center space-x-1 flex-shrink-0">
                            {/* Show edit button only when not selected, or show both when selected */}
                            {pendingCategory === category ? (
                              // When selected: show edit button first, then checkmark
                              <>
                                {hoverCategory === category && category !== "Uncategorized" && (
                                  <button
                                    onClick={(e) => startEditingCategory(e, category)}
                                    className={`p-0.5 ${getIconColor(category)} opacity-70 hover:opacity-100 transition-opacity duration-150 rounded`}
                                    aria-label={`Edit ${category} category`}
                                  >
                                    <PencilIcon className="h-3.5 w-3.5" />
                                  </button>
                                )}
                                <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                                  {HiOutlineCheck({ className: "w-6 h-6 text-gray-900 dark:text-white" })}
                                </div>
                              </>
                            ) : (
                              // When not selected: edit button takes checkmark position
                              <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                                {hoverCategory === category && category !== "Uncategorized" && (
                                  <button
                                    onClick={(e) => startEditingCategory(e, category)}
                                    className={`p-0.5 ${getIconColor(category)} opacity-70 hover:opacity-100 transition-opacity duration-150 rounded`}
                                    aria-label={`Edit ${category} category`}
                                  >
                                    <PencilIcon className="h-3.5 w-3.5" />
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>

                  {/* Add New Category Section - DISABLED FOR NOW - FUTURE FEATURE */}
                  {/* {isAddingNew ? (
                    <form onSubmit={handleAddNewCategory} className="mb-4">
                      <div className="flex gap-2">
                        <input
                          ref={newCategoryInputRef}
                          type="text"
                          value={newCategoryName}
                          onChange={(e) => setNewCategoryName(e.target.value)}
                          placeholder="Category name"
                          className="flex-1 px-3 py-2 border border-gray-300 dark:border-dark-border rounded-md text-sm bg-white dark:bg-dark-card text-gray-900 dark:text-dark-primary focus:outline-none focus:ring-2 focus:ring-blue-500"
                          onKeyDown={(e) => {
                            if (e.key === 'Escape') {
                              e.preventDefault();
                              setIsAddingNew(false);
                              setNewCategoryName('');
                            }
                          }}
                        />
                        <button
                          type="submit"
                          disabled={!newCategoryName.trim()}
                          className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                        >
                          Add
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setIsAddingNew(false);
                            setNewCategoryName('');
                          }}
                          className="px-3 py-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 text-sm"
                        >
                          Cancel
                        </button>
                      </div>
                    </form>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setIsAddingNew(true)}
                      className="flex w-full items-center justify-center px-3 py-2 text-sm text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/10 rounded-md transition-colors mb-4 border-2 border-dashed border-gray-300 dark:border-gray-600 hover:border-blue-300 dark:hover:border-blue-500"
                    >
                      <PlusIcon className="h-4 w-4 mr-2" />
                      Add new category
                    </button>
                  )} */}

                  {/* Action Buttons */}
                  <div className="flex justify-end gap-3 pt-2 border-t border-gray-200 dark:border-gray-700">
                    {pendingCategory && (
                      <button
                        type="button"
                        onClick={() => setPendingCategory(undefined)}
                        className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
                      >
                        Clear Selection
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={handleDone}
                      className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors font-medium"
                    >
                      Done
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
};

export default CategoryModal; 